import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadRequestError } from '@dvag/dfs-api-client';
import type { CustomError } from '@dvag/dfs-api-client';
import { useRef } from 'react';
import {
  FamilyApprovalTwoFAPersonCentricMutationType,
  KundanzuganTransactionStatusResponse,
  PersonCentricDetail,
} from '../type/twoFactor';
import { useNotification } from '../utils/notification/NotificationContext';
import { familyApprovalTwoFaApi } from '../service/api/familyApprovalTwoFaApi';
import { pollTransactionStatus } from './useFamilyApprovalTwoFa';

export const useFamilyApprovalTwoFaPersonCentric = () => {
  const { showError } = useNotification();
  const queryClient = useQueryClient();
  const familyApprovalTFAService = familyApprovalTwoFaApi();
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation<
    KundanzuganTransactionStatusResponse,
    CustomError,
    FamilyApprovalTwoFAPersonCentricMutationType,
    unknown
  >({
    mutationFn: async ({ keycloakId, personListOlderEighteen }) => {
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;

      if (typeof keycloakId === 'undefined') {
        throw new BadRequestError('Invalid keycloakId');
      }

      const confirmation: PersonCentricDetail[] = personListOlderEighteen.map((item) => ({
        firstName: item.firstName,
        lastName: item.lastName,
        duration: item?.sharingPeriod?.label ?? 'UNLIMITED',
      }));

      const approvalPersonCentricResponse =
        await familyApprovalTFAService.sendFamilyApprovalTwoFAPersonCentric(
          {
            keycloakId,
            confirmation,
          },
          signal,
        );

      return new Promise<KundanzuganTransactionStatusResponse>((resolve, reject) => {
        pollTransactionStatus(
          familyApprovalTFAService,
          approvalPersonCentricResponse.data,
          signal,
          resolve,
          reject,
          Date.now(),
        );
      });
    },
    onSuccess: async (_payloadSuccess, { keycloakId }) => {
      await queryClient.invalidateQueries({
        queryKey: [`familyApprovalTwoFa-personCentric-${keycloakId}`],
      });
    },
    onError: (err: CustomError) => {
      showError({ status: err?.status });
    },
    onSettled: () => {
      abortRef.current = null;
    },
  });

  const abort = () => {
    abortRef?.current?.abort();
  };

  return {
    ...mutation,
    abort,
  };
};
